import { useState, useCallback, useRef } from "react";
import { ToolContainer, FileDropZone, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { toJpeg } from "html-to-image";
import { PDFDocument } from "pdf-lib";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { ogLengthWarnings, validateLookupUrl, normalizeLookupUrl } from "./ogUtils";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Shows how a shared link will actually look on social platforms like X, Facebook, or LinkedIn, by previewing the Open Graph card built from the title, description, and image you provide. Download either card as a JPG, or both together as a PDF.`,
  how: `Fill in the title, description, and page URL, and either paste an image URL or drag and drop an image directly — the preview updates live for both the Facebook/LinkedIn-style card and the X (Twitter) card. Download either preview as a JPG image, or both as a single PDF.`,
  tip: `Check this before sharing an important link publicly — it's a quick way to catch a missing preview image or an outdated title before it's visible to everyone who sees the post. "Check a live page" reads a public page's current tags for you (the page itself isn't stored), or you can type the values in to preview a page that isn't published yet. Pages behind a login, or that block automated visitors, can't be read. If you pasted an image URL rather than dragging in a file, downloading the preview can occasionally fail — most image hosts don't allow their images to be exported this way from another site. Drag-dropping the image file directly avoids that limitation entirely.`,
};

/** Safely extracts a hostname for display -- new URL() throws on anything
 * that isn't a fully-formed, valid URL (a partial or malformed entry
 * while typing, for example), which previously crashed this component
 * outright since it was called unguarded directly in render. */
function safeHostname(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url || "example.com";
  }
}

/** A self-contained, inline placeholder image (encoded as a data URI,
 * not fetched from anywhere) shown as the default before someone
 * enters their own image -- deliberately NOT an external placeholder
 * service URL. The specific bug this replaces: via.placeholder.com,
 * the default used previously, is genuinely dead/unreliable (confirmed
 * directly -- unstable and intermittently offline, effectively
 * abandoned), so the preview looked broken from the moment the page
 * loaded, before a visitor had even entered their own image, since the
 * default placeholder itself failed to load and the onError handler
 * hides the image entirely on failure. An inline SVG has no external
 * dependency to ever go down again.
 */
const DEFAULT_PLACEHOLDER_IMAGE =
  "data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%221200%22%20height%3D%22630%22%3E%3Crect%20width%3D%221200%22%20height%3D%22630%22%20fill%3D%22%23e2e8f0%22%2F%3E%3Ctext%20x%3D%22600%22%20y%3D%22315%22%20font-family%3D%22sans-serif%22%20font-size%3D%2236%22%20fill%3D%22%2394a3b8%22%20text-anchor%3D%22middle%22%20dominant-baseline%3D%22middle%22%3E1200%20%C3%97%20630%3C%2Ftext%3E%3C%2Fsvg%3E";

export default function OgPreview() {
  const [title, setTitle] = useState("My Awesome Page");
  const [description, setDescription] = useState("This is a description of my page that will appear in social media previews.");
  // Starts empty, not pre-filled with the placeholder data URI itself
  // -- that URI is only what actually renders when this is empty (see
  // effectiveImage below), so the Input field stays clean with its own
  // "Paste an image URL..." placeholder text instead of showing a huge
  // raw data: URI string as its value.
  const [image, setImage] = useState("");
  const [url, setUrl] = useState("https://example.com/page");
  const effectiveImage = image || DEFAULT_PLACEHOLDER_IMAGE;
  const [liveUrl, setLiveUrl] = useState("");
  const [issues, setIssues] = useState<string[] | null>(null);
  const lookup = trpc.pageMeta.fetch.useMutation();
  const liveUrlError = validateLookupUrl(liveUrl);
  const lengthWarnings = ogLengthWarnings(title, description);
  const readLive = async () => {
    setIssues(null);
    if (liveUrlError) return;
    try {
      const m = await lookup.mutateAsync({ url: normalizeLookupUrl(liveUrl) });
      setTitle(m.title); setDescription(m.description); setImage(m.image); setUrl(m.url);
      setIssues(m.issues);
      toast.success("Read the page's tags");
    } catch (e) { toast.error(e instanceof Error ? e.message : "Couldn't read that page"); }
  };
  const fbCardRef = useRef<HTMLDivElement>(null);
  const twitterCardRef = useRef<HTMLDivElement>(null);

  const handleImageDrop = useCallback((files: File[]) => {
    const file = files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImage(reader.result as string);
    reader.readAsDataURL(file);
  }, []);

  const hostname = safeHostname(url);

  // skipFonts avoids html-to-image hanging on cross-origin web fonts; the timeout turns a
  // silent hang (e.g. a remote image host that never answers) into a visible error.
  const renderCard = (node: HTMLElement) => {
    const opts = { quality: 0.95, pixelRatio: 2, cacheBust: true, backgroundColor: "#ffffff", skipFonts: true, imagePlaceholder: DEFAULT_PLACEHOLDER_IMAGE };
    return Promise.race([
      toJpeg(node, opts),
      new Promise<string>((_, reject) => setTimeout(() => reject(new Error("export timed out")), 20000)),
    ]);
  };

  const downloadCardAsJpg = async (ref: React.RefObject<HTMLDivElement | null>, filename: string) => {
    if (!ref.current) return;
    try {
      const dataUrl = await renderCard(ref.current);
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      downloadBlob(blob, filename);
      toast.success("Preview downloaded!");
    } catch (err) {
      console.error("Failed to export preview card as JPG:", err);
      toast.error("Couldn't export the image. If the image is a pasted URL, drop the image file instead.");
    }
  };

  const downloadBothAsPdf = async () => {
    if (!fbCardRef.current || !twitterCardRef.current) return;
    try {
      const fbDataUrl = await renderCard(fbCardRef.current);
      const twitterDataUrl = await renderCard(twitterCardRef.current);
      const pdf = await PDFDocument.create();
      for (const dataUrl of [fbDataUrl, twitterDataUrl]) {
        const bytes = await (await fetch(dataUrl)).arrayBuffer();
        const jpgImage = await pdf.embedJpg(bytes);
        const page = pdf.addPage([jpgImage.width, jpgImage.height + 60]);
        page.drawText("OG / Social Preview — FreeDailyPro", { x: 20, y: jpgImage.height + 25, size: 16 });
        page.drawImage(jpgImage, { x: 0, y: 0, width: jpgImage.width, height: jpgImage.height });
      }
      const pdfBytes = await pdf.save();
      downloadBlob(new Blob([new Uint8Array(pdfBytes)], { type: "application/pdf" }), "og-preview.pdf");
      toast.success("PDF downloaded!");
    } catch (err) {
      console.error("Failed to export previews as PDF:", err);
      toast.error("Couldn't export the PDF. If the image is a pasted URL, drop the image file instead.");
    }
  };

  return (
    <ToolContainer>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div className="rounded-md border p-3 space-y-2">
            <Label htmlFor="og-live">Check a live page</Label>
            <div className="flex gap-2">
              <Input id="og-live" aria-invalid={!!liveUrlError} value={liveUrl} onChange={e => setLiveUrl(e.target.value)} onKeyDown={e => { if (e.key === "Enter") void readLive(); }} placeholder="https://yoursite.com/page" />
              <Button onClick={readLive} disabled={lookup.isPending || !liveUrl.trim() || !!liveUrlError}>{lookup.isPending ? "Reading…" : "Read tags"}</Button>
            </div>
            {liveUrlError && <p role="alert" className="text-xs text-destructive">{liveUrlError}</p>}
            <p className="text-xs text-muted-foreground">Reads the page's current Open Graph and Twitter tags and fills the preview below. The page isn't stored.</p>
            {issues && (issues.length ? (
              <NoticeBox><ul className="text-xs list-disc pl-4 space-y-0.5">{issues.map(i => <li key={i}>{i}</li>)}</ul></NoticeBox>
            ) : <p className="text-xs text-emerald-600">The page has the main share tags and they're a sensible length.</p>)}
          </div>
          <div><Label>Title</Label><Input aria-label="Title" value={title} onChange={e => setTitle(e.target.value)} /></div>
          <div><Label>Description</Label><Textarea aria-label="Description" value={description} onChange={e => setDescription(e.target.value)} className="h-20" /></div>
          <p className="text-xs text-muted-foreground">Title {title.length} characters, description {description.length} characters.</p>
          {lengthWarnings.length > 0 && (
            <NoticeBox role="status"><ul className="text-xs list-disc pl-4 space-y-0.5">{lengthWarnings.map(w => <li key={w}>{w}</li>)}</ul></NoticeBox>
          )}
          <div>
            <Label>Image</Label>
            <div className="space-y-2 mt-1.5">
              <Input value={image} onChange={e => setImage(e.target.value)} placeholder="Paste an image URL..." />
              <FileDropZone gateAtDownload accept="image/*" onFiles={handleImageDrop} label="Or drop an image here" description="Sets the preview image directly, no URL needed" />
            </div>
          </div>
          <div><Label>Page URL</Label><Input aria-label="Page URL" value={url} onChange={e => setUrl(e.target.value)} /></div>
        </div>
        <div className="space-y-4">
          <Label>Preview</Label>
          {/* Facebook/OG Preview */}
          <div>
            <div ref={fbCardRef} className="border rounded-lg overflow-hidden bg-card">
              <div className="aspect-[1200/630] bg-muted"><img src={effectiveImage} alt="Preview of how this page's image will look when shared on Facebook" className="w-full h-full object-cover" onError={e => (e.target as HTMLImageElement).style.display = "none"} /></div>
              <div className="p-3">
                <p className="text-xs text-muted-foreground uppercase">{hostname}</p>
                <p className="font-semibold text-sm mt-0.5 line-clamp-1">{title}</p>
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{description}</p>
              </div>
            </div>
            <Button size="sm" variant="outline" className="gap-1.5 mt-2" onClick={() => downloadCardAsJpg(fbCardRef, "og-preview-facebook.jpg")}>
              <Download className="h-3.5 w-3.5" /> Download as JPG
            </Button>
          </div>
          {/* Twitter Card Preview */}
          <div>
            <div ref={twitterCardRef} className="border rounded-xl overflow-hidden bg-card">
              <div className="aspect-[2/1] bg-muted"><img src={effectiveImage} alt="Preview of how this page's image will look when shared on Twitter/X" className="w-full h-full object-cover" onError={e => (e.target as HTMLImageElement).style.display = "none"} /></div>
              <div className="p-3">
                <p className="font-medium text-sm line-clamp-1">{title}</p>
                <p className="text-xs text-muted-foreground line-clamp-2">{description}</p>
                <p className="text-xs text-muted-foreground mt-1">{hostname}</p>
              </div>
            </div>
            <Button size="sm" variant="outline" className="gap-1.5 mt-2" onClick={() => downloadCardAsJpg(twitterCardRef, "og-preview-twitter.jpg")}>
              <Download className="h-3.5 w-3.5" /> Download as JPG
            </Button>
          </div>
          <Button className="gap-1.5 w-full" onClick={downloadBothAsPdf}>
            <Download className="h-4 w-4" /> Download Both as PDF
          </Button>
        </div>
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
